/* global Stripe */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import HairstylistDetail from '../components/HairstylistDetail';
import { useAuth } from '../contexts/AuthContext';
import { useFavorites } from '../hooks/useFavorites';
import { useReviews } from '../hooks/useReviews';
import { useTranslation } from '../hooks/useTranslation';
import AuthModal from '../components/AuthModal';
import ReviewModal from '../components/ReviewModal';
import { logProfileOpen } from '../utils/hairstylistAnalytics';
import styles from './HairstylistPage.module.css';
import { DEFAULT_PROVIDER_TYPE } from '../config/providerTypes';

const CREATE_CHECKOUT_SESSION_URL =
  'https://us-central1-cheriecoiffer-react.cloudfunctions.net/createCheckoutSession';
const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_REPLACE_ME';

function HairstylistPage() {
  const { id, type } = useParams();
  const providerType = type || 'hair';
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { language, t } = useTranslation();
  const [stylist, setStylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [upgradingToPro, setUpgradingToPro] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { userReview } = useReviews(id);

  const hasBackground = !!location.state?.background;

  // Scroll to top uniquement pour l'affichage plein écran
  useEffect(() => {
    if (!hasBackground) {
      window.scrollTo(0, 0);
    }
  }, [id, hasBackground]);

  useEffect(() => {
    const fetchStylist = async () => {
      try {
        const stylistDoc = await getDoc(doc(db, 'hairstylists', id));
        if (stylistDoc.exists()) {
          setStylist({ id: stylistDoc.id, ...stylistDoc.data() });
          
          // Log profile open event
          logProfileOpen(id, currentUser?.uid || null, 'direct_link');
        } else {
          // Si la coiffeuse n'existe pas, rediriger vers la page de recherche
          navigate(`/search/${providerType}`);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching stylist:', error);
        navigate(`/search/${providerType}`);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchStylist();
    }
  }, [id, navigate, currentUser]);

  useEffect(() => {
    const fetchCatalog = async () => {
      // Utiliser le type du profil s'il existe, sinon DEFAULT_PROVIDER_TYPE
      const providerType = stylist?.type || DEFAULT_PROVIDER_TYPE;
      try {
        const [categoriesSnap, servicesSnap] = await Promise.all([
          getDocs(query(collection(db, 'categories'), where('type', '==', providerType))),
          getDocs(query(collection(db, 'services'), where('type', '==', providerType))),
        ]);

        setCategories(
          categoriesSnap.docs
            .map((doc) => doc.data())
            .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
        );

        setServices(
          servicesSnap.docs
            .map((doc) => {
              const data = doc.data();
              // Normaliser les noms de champs: supporte prestationCode/prestation ET code/label
              return {
                ...data,
                code: data.code || data.prestationCode || '',
                label: data.label || data.prestation || '',
              };
            })
            .filter((s) => s.code && s.label)
            .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load catalog', err);
      }
    };

    fetchCatalog();
  }, [stylist?.type]);

  const serviceLabelLookup = useMemo(
    () =>
      services.reduce((acc, service) => {
        // Utiliser le champ 'en' si la langue n'est pas français et si le champ existe
        let label = service.label || service.prestation || '';
        if (language && language !== 'fr' && service.en) {
          label = service.en;
        }
        acc[service.code] = label;
        return acc;
      }, {}),
    [services, language],
  );

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.loading}>Chargement...</div>
        </div>
      </div>
    );
  }

  if (!stylist) {
    return null;
  }

  const isOwner = currentUser && stylist.userId === currentUser.uid;
  const isAdmin = currentUser?.displayName?.toLowerCase() === 'admin';
  const isFreeAccount = stylist.accountType === 'FREE';

  const handleUpgradeToPro = async () => {
    if (!currentUser || !isOwner) {
      setShowAuthModal(true);
      return;
    }

    if (!STRIPE_PUBLISHABLE_KEY || STRIPE_PUBLISHABLE_KEY === 'pk_test_REPLACE_ME') {
      alert("Stripe n'est pas encore configuré. Veuillez contacter le support.");
      return;
    }

    if (typeof Stripe === 'undefined') {
      alert("Impossible d'initialiser Stripe. Veuillez réessayer plus tard.");
      return;
    }

    setUpgradingToPro(true);
    try {
      const priceId = process.env.REACT_APP_STRIPE_PRICE_ID || 'price_REPLACE_ME';
      const customerEmail = currentUser.email || '';

      if (!customerEmail) {
        alert('Email manquant. Veuillez vérifier votre compte.');
        setUpgradingToPro(false);
        return;
      }

      const response = await fetch(CREATE_CHECKOUT_SESSION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId,
          customerEmail,
          existingProfileId: id, // Passer l'ID du profil existant
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        // eslint-disable-next-line no-console
        console.error('[HairstylistPage] createCheckoutSession failed', errorText);
        alert('Erreur lors de la création de la session de paiement. Veuillez réessayer.');
        setUpgradingToPro(false);
        return;
      }

      const data = await response.json();
      const { sessionId } = data;

      if (!sessionId) {
        // eslint-disable-next-line no-console
        console.error('[HairstylistPage] Réponse sans sessionId', data);
        alert('Erreur lors de la création de la session de paiement.');
        setUpgradingToPro(false);
        return;
      }

      const stripe = Stripe(STRIPE_PUBLISHABLE_KEY);
      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        // eslint-disable-next-line no-console
        console.error('[HairstylistPage] redirectToCheckout error', error);
        alert('Erreur lors de la redirection vers Stripe. Veuillez réessayer.');
        setUpgradingToPro(false);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error upgrading to PRO:', error);
      alert('Erreur lors de la mise à niveau. Veuillez réessayer.');
      setUpgradingToPro(false);
    }
  };

  return (
    <div className={`${styles.page} page-slide-enter`}>
      <div className={styles.container}>
        {/* Header mobile avec nom, flèche retour et favoris */}
        <div className={styles.mobileHeader}>
          <div className={styles.mobileHeaderTop}>
            <button
              type="button"
              className={styles.mobileBackButton}
              onClick={() => navigate(-1)}
              aria-label="Retour"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <h1 className={styles.mobileName}>{stylist.name}</h1>
            {!isOwner && (
              <button
                type="button"
                className={styles.mobileFavoriteButton}
                onClick={async () => {
                  if (!currentUser) {
                    setShowAuthModal(true);
                    return;
                  }
                  try {
                    await toggleFavorite(id);
                  } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('Error toggling favorite:', error);
                  }
                }}
                aria-label={isFavorite(id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill={isFavorite(id) ? 'currentColor' : 'none'} xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill={isFavorite(id) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"/>
                </svg>
              </button>
            )}
          </div>
          
          {/* Boutons modifier mon profil et passer en mode PRO (mobile) */}
          {(isOwner || isAdmin) && (
            <div className={styles.mobileActionButtons}>
              <button
                type="button"
                className={styles.mobileEditProfileBtn}
                onClick={() => navigate(`/hairstylist/edit/${stylist.id}`)}
              >
                {t('header.editProfile')}
              </button>
              {isOwner && isFreeAccount && stylist.paymentStatus !== 'pending' && (
                <button
                  type="button"
                  className={styles.mobileUpgradeToProBtn}
                  onClick={handleUpgradeToPro}
                  disabled={upgradingToPro}
                >
                  {upgradingToPro ? t('common.loading') : t('header.upgradeToPro')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Header desktop avec retour, nom, favoris et modifier */}
        <div className={styles.desktopHeader}>
          <div className={styles.desktopHeaderLeft}>
            <button
              type="button"
              className={styles.desktopBackButton}
              onClick={() => navigate(-1)}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h1 className={styles.desktopName}>{stylist.name}</h1>
          </div>
          <div className={styles.desktopHeaderRight}>
            {!isOwner && (
              <button
                type="button"
                className={styles.desktopFavoriteButton}
                onClick={async () => {
                  if (!currentUser) {
                    setShowAuthModal(true);
                    return;
                  }
                  try {
                    await toggleFavorite(id);
                  } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('Error toggling favorite:', error);
                  }
                }}
                aria-label={isFavorite(id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill={isFavorite(id) ? 'currentColor' : 'none'} xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill={isFavorite(id) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"/>
                </svg>
              </button>
            )}
            {(isOwner || isAdmin) && (
              <div className={styles.desktopActionButtons}>
                <button
                  type="button"
                  className={styles.editProfileBtn}
                  onClick={() => navigate(`/hairstylist/edit/${stylist.id}`)}
                >
                  {t('header.editProfile')}
                </button>
                {isOwner && isFreeAccount && stylist.paymentStatus !== 'pending' && (
                  <button
                    type="button"
                    className={styles.upgradeToProBtn}
                    onClick={handleUpgradeToPro}
                    disabled={upgradingToPro}
                  >
                    {upgradingToPro ? t('common.loading') : t('header.upgradeToPro')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Bandeau : non connecté sur un profil pending (venant de l'email) */}
        {!currentUser && stylist.paymentStatus === 'pending' && (
          <div className={styles.unpublishedWarning}>
            <svg className={styles.warningIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 20h20L12 2z" fill="currentColor"/>
              <path d="M12 10v4M12 16h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <div className={styles.warningContent}>
              <strong>{t('profile.signInToActivate')}</strong>
              <p>{t('profile.signInToActivateText')}</p>
              <button
                type="button"
                className={styles.warningButton}
                onClick={() => setShowAuthModal(true)}
              >
                {t('payment.signIn')}
              </button>
            </div>
          </div>
        )}
        {/* Bandeau d'avertissement pour profil non publié (propriétaire connecté) */}
        {isOwner && stylist.paymentStatus === 'pending' && (
          <div className={styles.unpublishedWarning}>
            <svg className={styles.warningIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 20h20L12 2z" fill="currentColor"/>
              <path d="M12 10v4M12 16h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <div className={styles.warningContent}>
              <strong>{t('profile.unpublishedTitle')}</strong>
              <p>{t('profile.unpublishedMessage')}</p>
              <button 
                type="button"
                className={styles.warningButton}
                onClick={handleUpgradeToPro}
                disabled={upgradingToPro}
              >
                {upgradingToPro ? t('common.loading') : t('profile.finalizePayment')}
              </button>
            </div>
          </div>
        )}

        <HairstylistDetail
          stylist={stylist}
          serviceLookup={serviceLabelLookup}
          categories={categories}
          services={services}
        />
      </div>
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      {showReviewModal && (
        <ReviewModal
          hairstylistId={id}
          onClose={() => setShowReviewModal(false)}
        />
      )}
    </div>
  );
}

export default HairstylistPage;

